import { observable, computed, action, observe } from 'mobx'
import { apis, request } from "@/utils/net"

const defaultInvoiceData = {
    client_info: {}, // 开票方（商户）信息
    buyer_info: {}, // 购方信息
    goods_info: [], // 商品（开票项目）信息
    invoice_info: {}, // 发票信息
}
const defaultBuyerInfo = {
    name: '', // 企业名称
    tax_code: '', // 纳税人识别号
    bank_name: '', // 开户行
    bank_account: '', // 帐号
    address: '', // 地址
    phone: '', // 电话
    email: '', // 邮箱
    // title_type: 2, // 抬头类型，1个人，2企业
}
const defaultClientInfo = {
    name: '', // 企业名称
    tax_code: '', // 纳税人识别号
    bank_name: '', // 开户行
    bank_account: '', // 帐号
    address: '', // 地址
    phone: '', // 电话
}
const defaultInvoiceInfo = {
    remark: '', // 备注
    drawer: '', // 开票人
    payee: '', // 收款人
    reviewer: '', // 复核人
}

class Store {
    // 缓存发票信息，以便在提交成功后重置数据
    _invoiceData = {}

    // 抬头类型，1个人，2企业
    // 这里独立维护，避免在切换抬头类型时，导致 invoiceData 变化，而更新了开票组件的页面数据
    @observable titleType = 2

    // 发票数据
    @observable.shallow invoiceData = {...defaultInvoiceData}

    // 酒店信息
    @observable.shallow hotelInfo = {}

    // 是否为通用酒店标识 1：万达 2:通用
    @observable commonType = 1

    // 是否显示实名信息授权组件
    @observable visibleRealnameInfoModal = false

    // 隐藏实名信息授权组件
    @action hideRealnameInfoModal() {
        this.visibleRealnameInfoModal = false
    }

    // 校验实名信息是否已存在
    @action checkRealnameInfo() {
        return new Promise((resolve, reject) => {
            request({
                url: apis.getRealnameInfo,
                unforeseen: () => {
                    this.visibleRealnameInfoModal = true
                    return reject()
                }
            }).then(() => {
                return resolve()
            })
        })
    }

    // 普通开票，获取开票方（商户）信息
    @action fetchMerchantInfo() {
        return request({
            url: apis.getMerchantInfo,
            unforeseen: () => {}
        }).then(res => {
            this._invoiceData = {
                ...defaultInvoiceData,
                buyer_info: {
                    ...defaultBuyerInfo,
                    // 抬头类型，取当前已缓存的值，避免数据不同步
                    title_type: this.titleType
                },
                client_info: {
                    name: res.name,
                    tax_code: res.tax_code,
                    phone: res.sell_phone,
                    bank_name: res.sell_bank_name,
                    bank_account: res.sell_bank_account,
                    address: res.sell_address
                },
                invoice_info: {
                    ...defaultInvoiceInfo,
                    payee: res.payee, // 收款人
                    reviewer: res.reviewer, // 复核人
                    drawer: res.drawer, // 开票人
                }
            }
            this.refreshInvoiceData()
            return res
        })
    }

    // 酒店开票，获取酒店订单信息
    @action fetchHotelInfo(orderSn, saleId, type) {
        this.commonType = type === 1 ? apis.getHotelInfo : apis.infoHotelCommon
        return request({
            url: this.commonType,
            method: 'post',
            data: {
                order_sn: orderSn,
                sale_id: saleId
            }
        }).then(res => {
            this.hotelInfo = { ...res, sale_id: saleId}
            // 转换到通用的invoiceData格式数据
            this._invoiceData = {
                ...defaultInvoiceData,
                buyer_info: {
                    title_type: res.buyer_title_type,
                    name: res.buyer_title,
                    tax_code: res.buyer_taxcode,
                    phone: res.buyer_address_phone,
                    email: res.buyer_email,
                    bank_name: res.buyer_bank_name,
                    bank_account: res.buyer_bank_account,
                    address: res.buyer_address
                },
                goods_info: [{
                    name: res.name,
                    tax_code: res.project_tax_code,
                    tax_rate: res.tax_rate,
                    id: res.project_id
                }],
                client_info: {
                    name: res.client_name,
                    tax_code: res.tax_code,
                    phone: res.phone,
                    bank_name: res.sell_bank_name,
                    bank_account: res.sell_bank_account,
                    address: `${res.address} ${res.area_name}`,
                },
                invoice_info: {
                    ...defaultInvoiceInfo,
                    payee: res.payee, // 收款人
                    reviewer: res.reviewer, // 复核人
                    drawer: res.drawer // 开票人
                }
            }

            // 酒店导入时带入用户信息
            if (saleId) {
                const hotelOrder = {
                    goods_info: [{
                        name: res.name,
                        tax_code: res.project_tax_code,
                        total_price_with_tax: res.total_price_with_tax,
                        tax_amount: res.tax_amount,
                        tax_rate: res.tax_rate,
                        id: res.project_id
                    }],
                    invoice_info: {
                        ...defaultInvoiceInfo,
                        payee: res.payee, // 收款人
                        reviewer: res.reviewer, // 复核人
                        drawer: res.drawer, // 开票人
                        total_price: res.total_price,
                        total_price_with_tax: res.total_price_with_tax,
                        total_rmb: res.total_rmb,
                        total_tax: res.tax_amount,
                    }
                }
                this._invoiceData = { ...this._invoiceData, ...hotelOrder}
            }

            this.refreshInvoiceData()
            return res
        })
    }

    @action refreshInvoiceData() {
        this.invoiceData = { ...this._invoiceData }
    }

    @action changeTitleType(titleType) {
        this.titleType = titleType
    }

    @action changeHotelInvoiceStyle(invoiceStyle) {
        this.hotelInfo = {
            ...this.hotelInfo,
            invoice_style: invoiceStyle
        }
    }

    /**
     * 开票
     */
    @action invoice(invoiceData) {
        return request({
            url: apis.submitInvoice,
            method: 'post',
            data: this._parseInvoiceData(invoiceData)
        })
    }

    @action printInvoice(orderSn) {
        return request({
            url: apis.printPaperInvoice,
            method: 'post',
            data: {
                order_sn: orderSn
            }
        })
    }

    /**
     * 酒店开票
     */
    @action hotelInvoice(orderSn, invoiceData) {
        const { hotel_date_at, hotel_room_no, hotel_user_name, invoice_style, sale_id, store_id} = this.hotelInfo
        const commonTypeApi = this.commonType === 1 ? apis.submitHotelInvoice : apis.hotelBlueCommon
        const params = {
            ...this._parseInvoiceData(invoiceData),
            order_sn: orderSn,
            invoice_style,
            hotel_date_at,
            hotel_room_no,
            hotel_user_name,
            // 酒店水单识别码
            sale_id,
            store_id
        }
        return request({
            url: commonTypeApi,
            method: 'post',
            data: params
        })
    }

    /**
     * 转换成老接口(提交开票接口)的开票数据
     */
    _parseInvoiceData(invoiceData) {
        const { buyer_info, client_info, goods_info, invoice_info } = invoiceData

        return {
            // 购方信息
            buyer_title_type: buyer_info.title_type,
            buyer_title: buyer_info.name,
            buyer_taxcode: buyer_info.tax_code,
            buyer_email: buyer_info.email,
            buyer_address: buyer_info.address,
            buyer_phone: buyer_info.phone,
            buyer_bank_name: buyer_info.bank_name,
            buyer_bank_account: buyer_info.bank_account,

            // 销方信息
            seller_name: client_info.name,
            taxpayer_num: client_info.tax_code,
            seller_address: client_info.address,
            seller_tel: client_info.phone,
            seller_bank_name: client_info.bank_name,
            seller_bank_account: client_info.bank_account,

            // 开票项目
            goods_info: JSON.stringify(goods_info),

            // 发票信息
            ...invoice_info,
            amount: invoice_info.total_price_with_tax,
            // 在线人工开票的备注字段用的extra
            extra: invoice_info.remark,

        }
    }
}

export default new Store()
